import React, { useEffect, useState } from "react";
import { api } from "../utils/api";

type Exercise = {
  id: string;
  name: string;
  sets?: number;
  reps?: number;
  duration?: string;
};
type Meal = { id: string; name: string; calories?: number };

interface Props {
  planId: string;
  type: "workout" | "food";
  onClose?: () => void;
}

const PlanDetailCard: React.FC<Props> = ({ planId, type, onClose }) => {
  const [detail, setDetail] = useState<{
    id: string;
    name: string;
    exercises?: Exercise[];
    meals?: Meal[];
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const path =
          type === "workout"
            ? `/api/user/plans/workout/${planId}`
            : `/api/user/plans/food/${planId}`;
        const data = await api.get<{
          id: string;
          name: string;
          exercises?: Exercise[];
          meals?: Meal[];
        }>(path);
        setDetail(data);
      } catch (e) {
        console.error("Failed to load plan detail", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [planId, type]);

  return (
    <div className="plan-detail-card" role="region" aria-label="Plan details">
      <div className="plan-detail-header">
        <h3>{detail?.name ?? "Plan Details"}</h3>
        <button
          className="close-btn"
          onClick={onClose}
          aria-label="Close plan details"
        >
          Close
        </button>
      </div>
      {loading && <div className="loading">Loading…</div>}
      {!loading && detail && (
        <>
          {detail.exercises && detail.exercises.length > 0 && (
            <div className="exercise-list">
              <h4>Exercises</h4>
              <ul>
                {detail.exercises.map((ex) => (
                  <li key={ex.id}>
                    {ex.name}
                    {ex.sets ? ` • ${ex.sets} sets` : ""}
                    {ex.reps ? ` x ${ex.reps} reps` : ""}
                    {ex.duration ? ` (${ex.duration})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {detail.meals && detail.meals.length > 0 && (
            <div className="meal-list">
              <h4>Meals</h4>
              <ul>
                {detail.meals.map((m) => (
                  <li key={m.id}>
                    {m.name}
                    {m.calories ? ` • ${m.calories} kcal` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!detail.exercises && !detail.meals && (
            <div className="empty">No items in this plan.</div>
          )}
        </>
      )}
    </div>
  );
};

export default PlanDetailCard;
